import React from 'react';
import { ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';

const TextWithTooltips = ({ text }) => {
    if (!text) return null;

    const segments = [];
    let currentIndex = 0;
    const tooltipRegex = /<tooltip>(.*?)<\/tooltip><tooltipExplanation>(.*?)<\/tooltipExplanation>/g;

    let match;
    while ((match = tooltipRegex.exec(text)) !== null) {
        if (match.index > currentIndex) {
            segments.push({
                type: 'text',
                content: text.substring(currentIndex, match.index),
            });
        }
        segments.push({
            type: 'tooltip',
            content: match[1],
            explanation: match[2],
        });
        currentIndex = match.index + match[0].length;
    }

    if (currentIndex < text.length) {
        segments.push({
            type: 'text',
            content: text.substring(currentIndex),
        });
    }

    return (
        <>
            {segments.map((segment, index) => (
                segment.type === 'tooltip' ? (
                    <span key={index} className="relative group cursor-help">
                        <span className="bg-[#002f69] text-white px-1 rounded">
                            {segment.content}
                        </span>
                        <div className="absolute hidden group-hover:block bottom-full mb-1 left-1/2 transform -translate-x-1/2 bg-black text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
                            {segment.explanation}
                        </div>
                    </span>
                ) : (
                    <span key={index}>{segment.content}</span>
                )
            ))}
        </>
    );
};

const JobDetails = ({ job, translations }) => {
    const { applyNow = 'Apply Now' } = translations;

    return (
        <div className="h-full flex flex-col overflow-hidden">
            <div className="flex flex-col h-full">
                {/* Header section */}
                <div className="p-6 pb-2 flex justify-between items-start">
                    <div className="flex items-center gap-5">
                        <img
                            src={job.logo}
                            alt={job.company}
                            className="w-20 h-20 rounded object-cover flex-shrink-0"
                        />
                        <div className="flex flex-col justify-center h-20 gap-0.5">
                            <h1 className="text-xl font-bold text-gray-900 leading-tight">
                                {job.title}
                            </h1>
                            <div className="text-gray-500 text-sm leading-tight">
                                <span>{job.company}</span>
                                <span className="mx-1 text-gray-400">,</span>
                                <span>{job.location}</span>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-1">
                                {job.badges.map((badge, index) => (
                                    <div key={index} className="relative group">
                                        <span
                                            className={`inline-flex text-xs font-medium px-3 py-1 rounded-md ${
                                                badge.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                                            }`}
                                        >
                                            {badge.text}
                                        </span>
                                        {badge.text.includes('Match') && job.matchReason && (
                                            <div className="absolute hidden group-hover:block top-full left-1/2 transform -translate-x-1/2 mt-1 bg-black text-white text-xs rounded py-2 px-3 min-w-[500px] max-w-[400px] z-10 whitespace-normal leading-relaxed">
                                                {job.matchReason}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Right top corner */}
                    <div className="flex flex-col items-end gap-2">
                        <div className="relative group">
                            <button
                                className="min-w-[128px] h-[40px] bg-green-600 hover:bg-green-600 text-white font-semibold text-sm px-4 rounded-md transition shadow-sm"
                                disabled
                            >
                                {job.matchScore}% Match
                            </button>
                            {job.matchReason && (
                                <div className="absolute hidden group-hover:block top-full right-0 mt-1 bg-black text-white text-xs rounded py-2 px-3 min-w-[300px] max-w-[400px] z-10 whitespace-normal leading-relaxed">
                                    {job.matchReason}
                                </div>
                            )}
                        </div>
                        <button
                            className="min-w-[128px] h-[40px] bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm px-4 rounded-md transition shadow-sm"
                            onClick={() => window.open(job.applyLink, '_blank')}
                        >
                            {applyNow}
                        </button>
                    </div>
                </div>

                {/* Horizontal Line */}
                <hr className="border-gray-300 mb-4 mx-6" />

                {/* Scrollable content */}
                <div className="flex-1 overflow-y-auto px-6 pb-6">
                    <div className="text-sm">
                        {/* Dynamic Sections */}
                        {job.sections && job.sections.map((section, sectionIndex) => (
                            <div key={sectionIndex} className="mb-6">
                                <h2 className="text-base font-semibold text-gray-900 mb-2">
                                    {section.title}
                                </h2>
                                {Array.isArray(section.content) ? (
                                    <ul className="list-disc pl-5 text-gray-700 space-y-1">
                                        {section.content.map((item, itemIndex) => (
                                            <li key={itemIndex}><TextWithTooltips text={item} /></li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-gray-700 leading-relaxed">
                                        <TextWithTooltips text={section.content} />
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default JobDetails;
